// @ts-nocheck
/**
 * lazy-video.js
 * ─────────────────────────────────────────────────────────────
 * Activates ambient <video data-lazy-video> elements rendered by
 * snippets/lazy-video.liquid. The markup ships with poster +
 * preload="none" and NO src — zero video bytes on page load.
 *
 * A video starts loading + playing only when:
 *   • the page has finished loading (never competes with LCP), and
 *   • it scrolls into view (IntersectionObserver), and
 *   • it is actually renderable — inactive tab layers sit at
 *     opacity:0/visibility:hidden but still intersect geometrically.
 *
 * The src is chosen at activation time: data-lv-src-mobile on small
 * viewports (smaller rendition), data-lv-src otherwise.
 *
 * Tab-style UIs whose switches don't change geometry must call
 * window.LazyVideo.activate(video) from their click handlers.
 * Content swapped in via innerHTML (PDP color change) is re-scanned
 * on the 'pdp:sectionsUpdated' document event.
 * ─────────────────────────────────────────────────────────────
 */
(function () {
  if (window.LazyVideo) return;

  var mqMobile = window.matchMedia("(max-width: 749px)");

  function isRenderable(v, ignoreOpacity) {
    if (typeof v.checkVisibility === "function") {
      return v.checkVisibility({
        visibilityProperty: true,
        // A tab clicked into view is mid opacity:0→1 fade, so its used opacity
        // is still ~0 at the synchronous moment we check — the opacity gate
        // would wrongly report it hidden and never start playback. Explicit
        // (tab-click) activations skip the gate (visibility has already snapped
        // visible); scroll-driven activations keep it so inactive tab layers,
        // which sit at opacity:0/visibility:hidden, never autoplay.
        opacityProperty: !ignoreOpacity,
      });
    }
    return !!v.getClientRects().length;
  }

  // Start playback only once the first frame is decoded. Calling play() on a
  // freshly-activated preload="none" video (nothing buffered yet) makes the
  // browser drop the poster before any frame exists — a black flash — then
  // stall while it buffers (the "flash → pause → play" the videos showed on
  // their first activation). Waiting for loadeddata lets the poster→first-frame
  // swap happen seamlessly, so playback begins from an already-painted frame.
  // _lvWant (cleared by the observer when the video scrolls out) prevents a
  // deferred play from starting a video that has since left view.
  function playWhenReady(v, explicit) {
    function start() {
      if (
        v._lvWant !== false &&
        document.visibilityState === "visible" &&
        isRenderable(v, explicit)
      ) {
        v.play().catch(function () {});
      }
    }
    if (v.readyState >= 2) {
      start();
      return;
    }
    if (v.dataset.lvWaiting) return; // don't stack loadeddata listeners
    v.dataset.lvWaiting = "1";
    var onReady = function () {
      v.removeEventListener("loadeddata", onReady);
      delete v.dataset.lvWaiting;
      start();
    };
    v.addEventListener("loadeddata", onReady);
    // The fetch is kicked off by prepare() (runs first in activate(), and
    // earlier via the preload observer). No load() here — a second load() would
    // abort prepare()'s in-flight fetch and restart it (visible "struggling").
  }

  // Begin downloading the video WITHOUT playing it. Called early by the preload
  // observer (lead time) so the file is buffered before the element is on
  // screen, then again (no-op) by activate() at play time.
  function prepare(v) {
    if (v.dataset.lvReady) return;
    var src = (mqMobile.matches && v.dataset.lvSrcMobile) || v.dataset.lvSrc;
    if (src && !v.getAttribute("src")) v.src = src;
    v.preload = "auto";
    v.dataset.lvReady = "1";
    v.load(); // start the fetch now (does not begin playback)
  }

  function activate(v, explicit) {
    v._lvWant = true;
    prepare(v);
    playWhenReady(v, explicit);
  }

  var hasIO = typeof IntersectionObserver !== "undefined";

  // PLAY observer: small margin → only play once the element is essentially on
  // screen, and pause (clearing play intent) the moment it leaves.
  var io = hasIO
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && isRenderable(entry.target)) {
              activate(entry.target);
            } else {
              entry.target._lvWant = false;
              entry.target.pause();
            }
          });
        },
        { rootMargin: "100px 0px", threshold: 0.1 },
      )
    : null;

  // PRELOAD observer: large margin → start fetching ~2 viewports ahead so the
  // video is buffered before the user reaches it (no "loads while you watch").
  // It only downloads — playback stays gated by the play observer, so nothing
  // plays off screen. Still zero video bytes on first paint: the element must
  // scroll within range first.
  var preloadIo = hasIO
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              prepare(entry.target);
              preloadIo.unobserve(entry.target); // download only needs to start once
            }
          });
        },
        { rootMargin: "200% 0px", threshold: 0 },
      )
    : null;

  function observe(v) {
    if (hasIO) {
      preloadIo.observe(v);
      io.observe(v);
    } else {
      activate(v);
    }
  }

  var pageLoaded = document.readyState === "complete";
  var pending = [];

  function bind(v) {
    if (v.dataset.lvBound) return; // idempotent across re-scans
    v.dataset.lvBound = "1";
    if (!pageLoaded) {
      pending.push(v);
      return;
    }
    observe(v);
  }

  function scan(root) {
    (root || document)
      .querySelectorAll("video[data-lazy-video]")
      .forEach(bind);
  }

  window.addEventListener(
    "load",
    function () {
      pageLoaded = true;
      pending.splice(0).forEach(function (v) {
        observe(v);
      });
    },
    { once: true },
  );

  // PDP color swap replaces [data-pdp-section] innerHTML — bind the new videos.
  document.addEventListener("pdp:sectionsUpdated", function () {
    scan();
  });

  // Resume when returning to the tab, and after bfcache restoration. Only
  // play while on screen — fighting the browser's power-saving pause is what
  // causes the flicker / "start and stop" effect.
  function resumeVisible() {
    document
      .querySelectorAll("video[data-lazy-video][data-lv-ready]")
      .forEach(function (v) {
        playWhenReady(v);
      });
  }
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") resumeVisible();
  });
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) resumeVisible();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      scan();
    });
  } else {
    scan();
  }

  // Tab UIs call activate() the instant they switch a tab in — flag it explicit
  // so the opacity-fade gate is skipped (see isRenderable). The internal
  // scroll/observer path calls activate() with no flag and stays strict.
  window.LazyVideo = {
    scan: scan,
    activate: function (v) {
      activate(v, true);
    },
  };
})();
